import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import ActionPathField from './ActionPathField';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionScope } from './ActionScopeContext';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { appendFileNameToFolderPath, normalizePathValue } from '../../Utils/pathUtils';

const ActionsCreateFile = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  initialScope = 'event',
}) => {
  const { t } = useTranslation();
  const currentScope = useActionScope();
  const [target, setTarget] = useState('');
  const [useCustomContent, setUseCustomContent] = useState(false);
  const [useGenContent, setUseGenContent] = useState(false);
  const [customContent, setCustomContent] = useState('');
  const [genContent, setGenContent] = useState('');
  const [browsingDisabled, setBrowsingDisabled] = useState(false);

  const customDataTags = useMemo(() => ([
    {
      key: 'tag_fileContentToWrite',
      label: t('workflow.create_file_existing_content_tag', 'Existing target file content'),
      value: 'fileContentToWrite',
      description: t(
        'workflow.create_file_existing_content_tag_help',
        'Content of the target file when it already exists',
      ),
    },
  ]), [t]);

  const availableTags = useMemo(
    () => (Array.isArray(tags) ? [...tags, ...customDataTags] : customDataTags),
    [customDataTags, tags],
  );

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'createfile',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      normalizePathValue(target) === normalizePathValue(initial.target)
      && useCustomContent === Boolean(initial.useCustomContent)
      && useGenContent === Boolean(initial.useGenContent)
      && customContent === (initial.customContent || '')
      && genContent === (initial.genContent || '')
    ),
    customSaveData: () => ({
      target: normalizePathValue(target),
      useCustomContent,
      useGenContent,
      customContent,
      genContent,
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setTarget(customData.target || '');
      setUseCustomContent(Boolean(customData.useCustomContent));
      setUseGenContent(Boolean(customData.useGenContent));
      setCustomContent(customData.customContent || '');
      setGenContent(customData.genContent || '');
    },
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setTarget(appendFileNameToFolderPath(folderPath, '{filename}.{file_extension}'));
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    },
  });

  const isProjectScope = currentScope === 'project' || initialScope === 'project';
  const targetMissing = !target.trim();
  const contentSelectionMissing = !isProjectScope && !useCustomContent && !useGenContent;
  const customContentMissing = useCustomContent && !customContent.trim();
  const generatedContentMissing = useGenContent && !genContent.trim();
  const hasValidationError = targetMissing
    || contentSelectionMissing
    || customContentMissing
    || generatedContentMissing;

  const handleBrowseClick = useCallback(() => {
    setBrowsingDisabled(true);
    handleBrowseDirectory();
  }, [handleBrowseDirectory]);

  const handleCustomContentChange = (event) => {
    const checked = event.target.checked;
    setUseCustomContent(checked);
    if (checked) {
      setUseGenContent(false);
    }
  };

  const handleGeneratedContentChange = (event) => {
    const checked = event.target.checked;
    setUseGenContent(checked);
    if (checked) {
      setUseCustomContent(false);
    }
  };

  return (
    <>
      <ActionSettingsLayout
        title={t('createfile', 'Create File')}
        isEnabled={actionSettings.isEnabled}
        onEnabledChange={actionSettings.setIsEnabled}
        isSaveDisabled={actionSettings.isSaveDisabled}
        hasValidationError={hasValidationError}
        onSave={actionSettings.handleSave}
        onForceRun={actionSettings.handleForceRun}
        forceRunLoading={actionSettings.forceRunLoading}
        forceRunResult={actionSettings.forceRunResult}
        popUpOpen={actionSettings.popUpOpen}
        onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
        conditions={actionSettings.conditions}
        onConditionsChange={actionSettings.handleConditionsChange}
        tags={tags}
      >
        <Grid item xs={12}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title={t('workflow.create_file_target_title', 'File destination')}
              titleTypographyProps={{
                sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 },
              }}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <ActionPathField
                browseDisabled={browsingDisabled}
                browseLabel={t('workflow.create_file_target_browse', 'Browse folder')}
                caseid={caseid}
                error={targetMissing}
                helperText={targetMissing
                  ? t('workflow.create_file_target_required', 'File destination is required.')
                  : t(
                    'workflow.create_file_target_help',
                    'Choose the full output path. Dynamic tags are supported.',
                  )}
                label={t('workflow.create_file_target_label', 'Destination path')}
                onBrowse={handleBrowseClick}
                onChange={setTarget}
                placeholder={t(
                  'workflow.create_file_target_placeholder',
                  'C:\\Output\\{filename}.{file_extension}',
                )}
                tags={availableTags}
                value={target}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={t('workflow.create_file_content_title', 'File content')}
              titleTypographyProps={{
                sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 },
              }}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('workflow.create_file_content_mode_label', 'Content mode')}
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={useCustomContent}
                        onChange={handleCustomContentChange}
                      />
                    )}
                    label={t('workflow.create_file_custom_content', 'Use custom content')}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={useGenContent}
                        onChange={handleGeneratedContentChange}
                      />
                    )}
                    label={t('workflow.create_file_generated_content', 'Use AI-generated content')}
                  />
                  {contentSelectionMissing && (
                    <FormHelperText error>
                      {t(
                        'workflow.create_file_content_required',
                        'Choose custom or AI-generated content for an event action.',
                      )}
                    </FormHelperText>
                  )}
                  {isProjectScope && !useCustomContent && !useGenContent && (
                    <FormHelperText>
                      {t(
                        'workflow.create_file_empty_project_help',
                        'No content mode selected: this project action will create an empty file.',
                      )}
                    </FormHelperText>
                  )}
                </Box>

                {useCustomContent && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('workflow.create_file_custom_content_label', 'Custom content')}
                    </Typography>
                    <TagEditor
                      caseid={caseid}
                      value={customContent}
                      onChange={setCustomContent}
                      placeholder={t(
                        'workflow.create_file_custom_content_placeholder',
                        'Enter the file content. Dynamic tags are supported.',
                      )}
                      tags={availableTags}
                    />
                    <FormHelperText error={customContentMissing}>
                      {customContentMissing
                        ? t('workflow.create_file_custom_content_required', 'Custom content is required.')
                        : t(
                          'workflow.create_file_custom_content_help',
                          'The resolved content will be written to the destination file.',
                        )}
                    </FormHelperText>
                  </Box>
                )}

                {useGenContent && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('workflow.create_file_generated_prompt_label', 'Generation instructions')}
                    </Typography>
                    <TagEditor
                      caseid={caseid}
                      value={genContent}
                      onChange={setGenContent}
                      placeholder={t(
                        'workflow.create_file_generated_prompt_placeholder',
                        'Describe the content to generate. Dynamic tags are supported.',
                      )}
                      tags={availableTags}
                    />
                    <FormHelperText error={generatedContentMissing}>
                      {generatedContentMissing
                        ? t(
                          'workflow.create_file_generated_prompt_required',
                          'Generation instructions are required.',
                        )
                        : t(
                          'workflow.create_file_generated_prompt_help',
                          'The generated response will be written to the destination file.',
                        )}
                    </FormHelperText>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </ActionSettingsLayout>

      {FolderBrowserComponent}
    </>
  );
};

export default React.memo(ActionsCreateFile);
